const BOTTOM_PANE_OPEN_STORAGE_KEY = "izanagi-bottom-pane-open";
const BOTTOM_PANE_TAB_STORAGE_KEY = "izanagi-bottom-pane-tab";
const SEGMENTATION_PANE_STATE_STORAGE_KEY = "izanagi-segmentation-pane-state";
const SEGMENTATION_PANE_STATE_VERSION = 3;
const SEGMENTATION_PANE_STATE_MAX_BYTES = 4096;

/** 下部ペインで選択できる親タブ。 */
export type BottomPaneTab =
	| "terminal"
	| "problems"
	| "monitors"
	| "tasks"
	| "knowledgeGraph"
	| "segmentation";
export type RestorableBottomPaneTab = Exclude<BottomPaneTab, "segmentation">;

/** 未解決の request_segmentation を、チャットへ戻ったときにどう表示し直すか。 */
export interface SegmentationPaneShowState {
	toolUseId: string;
	/** 再読み込み・チャット復帰後もセグメンテーションを前面へ戻すか。 */
	showSegmentation: boolean;
	/** セグメンテーション終了後に下部ペインを開いた状態へ戻すか (開始前の値で凍結する)。 */
	restoreOpen: boolean;
}

/** チャット単位の記録。複数チャットで同時に HITL が未解決でも互いを消さない。 */
interface SegmentationPaneRecords {
	version: typeof SEGMENTATION_PANE_STATE_VERSION;
	records: Record<string, SegmentationPaneShowState>;
}

function resolveSessionStorage(): Storage | null {
	try {
		return typeof window === "undefined" ? null : window.sessionStorage;
	} catch {
		return null;
	}
}

function write(storage: Storage, key: string, value: string): void {
	try {
		storage.setItem(key, value);
	} catch {
		// sessionStorage が使えない環境でも、下部ペインの操作自体は通常どおり続けられるようにする。
	}
}

function remove(storage: Storage, key: string): void {
	try {
		storage.removeItem(key);
	} catch {
		// sessionStorage が利用できない環境でも、下部ペイン自体は通常どおり利用できるようにする。
	}
}

function isRequestIdentity(value: unknown): value is string {
	return typeof value === "string" && value.trim().length > 0 && value.length <= 512;
}

function isBottomPaneTab(value: unknown): value is BottomPaneTab {
	return (
		value === "terminal" ||
		value === "problems" ||
		value === "monitors" ||
		value === "tasks" ||
		value === "knowledgeGraph" ||
		value === "segmentation"
	);
}

function isRestorableBottomPaneTab(value: unknown): value is RestorableBottomPaneTab {
	return isBottomPaneTab(value) && value !== "segmentation";
}

function isSegmentationPaneShowState(value: unknown): value is SegmentationPaneShowState {
	if (typeof value !== "object" || value === null) return false;
	const state = value as Partial<SegmentationPaneShowState>;
	return (
		isRequestIdentity(state.toolUseId) &&
		typeof state.showSegmentation === "boolean" &&
		typeof state.restoreOpen === "boolean"
	);
}

function isSegmentationPaneRecords(value: unknown): value is SegmentationPaneRecords {
	if (typeof value !== "object" || value === null) return false;
	const { version, records } = value as Partial<SegmentationPaneRecords>;
	if (version !== SEGMENTATION_PANE_STATE_VERSION) return false;
	if (typeof records !== "object" || records === null || Array.isArray(records)) return false;
	return Object.entries(records).every(
		([chatId, state]) => isRequestIdentity(chatId) && isSegmentationPaneShowState(state),
	);
}

function exceedsMaxBytes(raw: string): boolean {
	return new TextEncoder().encode(raw).byteLength > SEGMENTATION_PANE_STATE_MAX_BYTES;
}

/** 上限に収まる JSON を返す。収まらなければ null。 */
function serializeRecords(records: Record<string, SegmentationPaneShowState>): string | null {
	const raw = JSON.stringify({ version: SEGMENTATION_PANE_STATE_VERSION, records });
	return exceedsMaxBytes(raw) ? null : raw;
}

/** 保存済みの記録を読む。旧 version・不正値・上限超過はまとめて削除して無視する。 */
function readSegmentationPaneRecords(storage: Storage): SegmentationPaneRecords | null {
	let raw: string | null;
	try {
		raw = storage.getItem(SEGMENTATION_PANE_STATE_STORAGE_KEY);
	} catch {
		return null;
	}
	if (raw === null) return null;
	if (exceedsMaxBytes(raw)) {
		remove(storage, SEGMENTATION_PANE_STATE_STORAGE_KEY);
		return null;
	}

	try {
		const value: unknown = JSON.parse(raw);
		if (!isSegmentationPaneRecords(value)) {
			remove(storage, SEGMENTATION_PANE_STATE_STORAGE_KEY);
			return null;
		}
		return value;
	} catch {
		remove(storage, SEGMENTATION_PANE_STATE_STORAGE_KEY);
		return null;
	}
}

/** 下部ペインの開閉状態。同じブラウザタブ内の再読み込みでのみ復元する。 */
export function readBottomPaneOpen(storage: Storage | null = resolveSessionStorage()): boolean {
	if (!storage) return false;
	try {
		return storage.getItem(BOTTOM_PANE_OPEN_STORAGE_KEY) === "true";
	} catch {
		return false;
	}
}

export function writeBottomPaneOpen(
	open: boolean,
	storage: Storage | null = resolveSessionStorage(),
): void {
	if (!storage) return;
	write(storage, BOTTOM_PANE_OPEN_STORAGE_KEY, String(open));
}

/** 最後に手動で選んだ親タブ。未保存・不正値はターミナルとして扱う。 */
export function readBottomPaneTab(
	storage: Storage | null = resolveSessionStorage(),
): RestorableBottomPaneTab {
	if (!storage) return "terminal";
	let raw: string | null;
	try {
		raw = storage.getItem(BOTTOM_PANE_TAB_STORAGE_KEY);
	} catch {
		return "terminal";
	}
	return isRestorableBottomPaneTab(raw) ? raw : "terminal";
}

export function writeBottomPaneTab(
	tab: RestorableBottomPaneTab,
	storage: Storage | null = resolveSessionStorage(),
): void {
	if (!storage) return;
	write(storage, BOTTOM_PANE_TAB_STORAGE_KEY, tab);
}

/** 指定チャットの再表示記録を読み取る。 */
export function readSegmentationPaneShowState(
	chatId: string,
	storage: Storage | null = resolveSessionStorage(),
): SegmentationPaneShowState | null {
	if (!isRequestIdentity(chatId) || !storage) return null;
	return readSegmentationPaneRecords(storage)?.records[chatId] ?? null;
}

/** 指定チャットの再表示記録を保存する。上限を超える場合は他チャットの記録を退避する。 */
export function writeSegmentationPaneShowState(
	chatId: string,
	state: SegmentationPaneShowState,
	storage: Storage | null = resolveSessionStorage(),
): void {
	if (!isRequestIdentity(chatId) || !isSegmentationPaneShowState(state) || !storage) return;
	const current = readSegmentationPaneRecords(storage);
	const raw =
		serializeRecords({ ...current?.records, [chatId]: state }) ??
		serializeRecords({ [chatId]: state });
	if (raw === null) return;
	write(storage, SEGMENTATION_PANE_STATE_STORAGE_KEY, raw);
}

/** 指定チャットの再表示記録だけを削除する。 */
export function clearSegmentationPaneShowState(
	chatId: string,
	storage: Storage | null = resolveSessionStorage(),
): void {
	if (!isRequestIdentity(chatId) || !storage) return;
	const current = readSegmentationPaneRecords(storage);
	if (!current || !(chatId in current.records)) return;
	const records = { ...current.records };
	delete records[chatId];
	if (Object.keys(records).length === 0) {
		remove(storage, SEGMENTATION_PANE_STATE_STORAGE_KEY);
		return;
	}
	const raw = serializeRecords(records);
	if (raw === null) return;
	write(storage, SEGMENTATION_PANE_STATE_STORAGE_KEY, raw);
}
